// =============================================================================
// app/src/componentes/FeedDeLotes.tsx — o histórico das importações de PDF.
//
// Pedido do dono (25/09/2026): "gostaria de ter um feed das contas adicionadas
// por histórico, ao lado direito, podendo clicar em uma versão para voltar
// para ela. Só o dono pode fazer isso."
//
// O QUE ELE PEDIU E O QUE ESTÁ AQUI, e a diferença é deliberada. "Voltar para
// uma versão" existe no mapa da loja (084) e funciona porque a planta é um
// DOCUMENTO: ela cabe numa linha, e trocá-la não deixa nada pendurado. Conta a
// pagar é REGISTRO com dinheiro grudado — paga, com boleto anexado, com
// lançamento no caixa. Rebobinar a tabela até um instante no tempo deixaria
// tudo isso órfão.
//
// Então o "voltar" aqui é CANCELAR AQUELA IMPORTAÇÃO: mesma ideia de estado
// novo que não apaga o anterior, aplicada a registro em vez de documento. E ele
// diz na hora o que NÃO pôde desfazer — conta já paga e ocorrência de conta
// fixa ficam de pé, com o motivo.
//
// A COLUNA LATERAL EMPILHA NO CELULAR. O molde é o mapa da loja
// (`lg:grid-cols-[minmax(0,1fr)_23rem]`), e não o carrinho da Venda, que
// esconde a lateral no telefone: o dono lê isto no iPhone e precisa alcançar o
// feed lá.
// =============================================================================
import { useState } from 'react';
import type { LoteDeDocumentos, ResultadoDoCancelamento } from '@core/types';
import Aviso from './Aviso';
import Carregando from './Carregando';
import { brl } from '../lib/formato';

const dataHora = (iso: string) => {
  const [d, h] = iso.split('T');
  return `${d!.split('-').reverse().join('/')} ${(h ?? '').slice(0, 5)}`;
};

export default function FeedDeLotes({ lotes, carregando, erro, recusas, cancelando, aoCancelar }: {
  lotes: LoteDeDocumentos[];
  carregando: boolean;
  erro: string | null;
  /** O que o último cancelamento NÃO conseguiu desfazer. */
  recusas: ResultadoDoCancelamento[];
  cancelando: number | null;
  aoCancelar: (lote: number, motivo: string) => void;
}) {
  const [confirmando, setConfirmando] = useState<number | null>(null);
  const [motivo, setMotivo] = useState('');

  return (
    <section className="cartao" aria-label="Documentos importados">
      <div className="p-3">
        <p className="rotulo">PDFs do dia</p>
        <p className="text-sm text-mute">
          Cada PDF importado é uma leva. Cancelar uma leva <strong>não apaga</strong>:
          as contas ficam marcadas como canceladas, com o documento junto, e dá
          para reativar uma a uma na aba “Canceladas”.
        </p>
      </div>

      {carregando && <div className="px-3 pb-3"><Carregando texto="Lendo o histórico" /></div>}
      {erro && <div className="px-3 pb-3"><Aviso tipo="erro">{erro}</Aviso></div>}

      {recusas.length > 0 && (
        <div className="px-3 pb-3">
          <Aviso tipo="alerta">
            <strong>O que não deu para cancelar:</strong>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {recusas.map((r) => (
                <li key={r.conta_id}>{r.descricao} — {r.porque}</li>
              ))}
            </ul>
          </Aviso>
        </div>
      )}

      {!carregando && !erro && lotes.length === 0 && (
        <p className="border-t border-edge px-3 py-3 text-xs text-dim">
          Nenhum PDF importado ainda. Use “Documentos do dia” para mandar o PDF
          com tudo que você digitalizou.
        </p>
      )}

      {lotes.length > 0 && (
        <ul className="divide-y divide-edge border-t border-edge">
          {lotes.map((l) => (
            <li key={l.id} className="px-3 py-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{l.arquivo}</p>
                  <p className="text-xs text-mute tabular-nums">
                    {dataHora(l.criado_em)} · {l.paginas} folha{l.paginas > 1 ? 's' : ''} ·{' '}
                    {/* "2 de 3" só quando alguma saiu: a leva inteira de pé não
                        precisa da fração, e a fração é o que mostra que houve
                        cancelamento sem obrigar a abrir nada. */}
                    {l.contas_vivas === l.contas
                      ? `${l.contas} conta${l.contas === 1 ? '' : 's'}`
                      : `${l.contas_vivas} de ${l.contas} contas de pé`}{' '}
                    · {brl(Number(l.total))}
                  </p>
                  <p className="text-[0.6875rem] text-dim">
                    por {l.criado_por_nome ?? 'alguém que saiu da equipe'}
                    {l.contas_pagas > 0 && ` · ${l.contas_pagas} já paga${l.contas_pagas > 1 ? 's' : ''}`}
                  </p>
                  {l.cancelado_em && (
                    <p className="text-[0.6875rem] text-erro">
                      leva cancelada em {dataHora(l.cancelado_em)}
                      {l.cancelado_por_nome ? ` por ${l.cancelado_por_nome}` : ''}
                    </p>
                  )}
                </div>
                {l.contas_vivas > 0 && confirmando !== l.id && (
                  <button type="button" className="botao-terciario shrink-0 text-xs"
                          disabled={cancelando !== null}
                          onClick={() => { setConfirmando(l.id); setMotivo(''); }}>
                    Cancelar leva
                  </button>
                )}
              </div>

              {confirmando === l.id && (
                /* O motivo é obrigatório no banco, e é de propósito: o feed sem
                   motivo não conta história nenhuma daqui a um mês. */
                <div className="mt-2 space-y-2 border border-erro/40 bg-erro/5 p-2">
                  <p className="text-xs text-mute">
                    Cancela as {l.contas_vivas} conta{l.contas_vivas > 1 ? 's' : ''} desta leva que
                    ainda estão de pé. O documento de cada uma fica.
                  </p>
                  <input className="campo py-1.5 text-sm" value={motivo} autoFocus
                         placeholder="Por quê? (ex.: importei o PDF duas vezes)"
                         onChange={(e) => setMotivo(e.target.value)} />
                  <div className="flex gap-2">
                    <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                            onClick={() => setConfirmando(null)}>Voltar</button>
                    <button type="button" className="botao-perigo flex-1 py-1.5 text-xs disabled:opacity-50"
                            disabled={!motivo.trim() || cancelando !== null}
                            onClick={() => { aoCancelar(l.id, motivo.trim()); setConfirmando(null); }}>
                      {cancelando === l.id ? 'Cancelando…' : 'Cancelar a leva'}
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
